import axios, { AxiosRequestConfig, CancelToken, CancelTokenSource } from 'axios';
import { CreditCardAssignment, CreditCardTransaction } from '../pages/creditCards/CreditCardAPI';
import ExpenseReport from '../pages/expenses/ExpenseReport';
import { ExpenseReportHeader } from '../pages/expenses/ExpensesAPI';
import { FileUploads } from '../pages/files/FileAPI';
import Timesheet from '../pages/timesheets/Timesheet';
import { TimeRecordHeader } from '../pages/timesheets/TimesheetAPI';
import { CostCode, CYMAJob, EarningsType, Skill } from './CYMAAPI';
import AxiosConfig, { storeBaseURL } from './AxiosConfig';
import { CreatedUpdatedTable } from './APIReturnTypes';

const axiosInstance = AxiosConfig.instance();

const TokenGenerator = axios.CancelToken;

/*----------------------------------Cancel Tokens----------------------------------------*/

export const cancelPromises = (source: CancelTokenSource) => {
  source.cancel();
};

export const getSource = (): CancelTokenSource => {
  return TokenGenerator.source();
};

export const getCancelToken = (source: CancelTokenSource): CancelToken => {
  return source?.token;
};

/*----------------------------------Authentication----------------------------------------*/

//Send authentication info and set the return value to the local state
//saves between browser runs
export const authenticate = (username: string, password: string, rememberMe: boolean, token?: CancelToken): Promise<Employee> => {
  const url = '/authentication';

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    cancelToken: token !== undefined ? token : undefined,
  };

  let authPacket = 'username=' + encodeURIComponent(username) + '&password=' + encodeURIComponent(password);

  return axiosInstance
    .post(url, authPacket, configAuth)
    .catch((err) => {
      if (err.message === 'Network Error') {
        storeBaseURL('prod');
        configAuth.cancelToken = undefined;
        return axiosInstance.post(url, authPacket, configAuth);
      }
      return Promise.reject(err);
    })
    .then((response) => response.data)
    .then((data: any) => {
      sessionStorage.setItem('accessToken', data.token);
      sessionStorage.setItem('accessTokenExpiration', data.tokenExpiration);
      sessionStorage.setItem('refreshToken', data.refreshToken);
      sessionStorage.setItem('refreshTokenExpiration', data.refreshTokenExpiration);
      if (rememberMe) localStorage.setItem('accessToken', data.token);
      if (rememberMe) localStorage.setItem('accessTokenExpiration', data.tokenExpiration);
      if (rememberMe) localStorage.setItem('refreshToken', data.refreshToken);
      if (rememberMe) localStorage.setItem('refreshTokenExpiration', data.refreshTokenExpiration);
      return new Employee(data.user);
    });
};

//no call to api, but sets local state back to deauth
export const deAuthenticate = () => {
  const url = '/logout';

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  let body = 'RefreshToken=' + encodeURIComponent(sessionStorage.getItem('refreshToken') ?? '');

  return axiosInstance.post(url, body, configAuth);
};

//Impersonate a user and swap your auth token with theirs
export const impersonate = (userId: string): Promise<Employee> => {
  const url = '/impersonate';

  let config = {
    params: { userId },
  };

  return axiosInstance
    .post(url, null, config)
    .then((response) => response.data)
    .then((data: any) => {
      sessionStorage.setItem('accessToken', data.token);
      sessionStorage.setItem('accessTokenExpiration', data.tokenExpiration);
      sessionStorage.setItem('refreshToken', data.refreshToken);
      sessionStorage.setItem('refreshTokenExpiration', data.refreshTokenExpiration);
      const employee = new Employee(data.user);
      employee.isImpersonating = true;
      return employee;
    });
};

//undo impersonation
export const unImpersonate = (): Promise<Employee> => {
  const url = '/unimpersonate';

  return axiosInstance
    .post(url)
    .then((response) => response.data)
    .then((data: any) => {
      sessionStorage.setItem('accessToken', data.token);
      sessionStorage.setItem('accessTokenExpiration', data.tokenExpiration);
      sessionStorage.setItem('refreshToken', data.refreshToken);
      sessionStorage.setItem('refreshTokenExpiration', data.refreshTokenExpiration);
      return new Employee(data.user);
    });
};

/*------------------------------------Accounts-------------------------------------------*/

export const getUsers = (
  includeCards?: boolean,
  includeDirectReports?: boolean,
  includeFiles?: boolean,
  includeJobsAndCostCodes?: boolean,
  includeClosedCostCodes?: boolean,
  includeClosedJobs?: boolean,
  includeDisabled?: boolean,
  token?: CancelToken
): Promise<Employee[]> => {
  const url = '/accounts';

  var config = {
    headers: {},
    cancelToken: token !== undefined ? token : undefined,
    params: {} as any,
  };

  if (includeCards !== undefined) config.params.includeCards = includeCards;
  if (includeDirectReports !== undefined) config.params.includeDirectReports = includeDirectReports;
  if (includeFiles !== undefined) config.params.includeFiles = includeFiles;
  if (includeJobsAndCostCodes !== undefined) config.params.includeJobsAndCostCodes = includeJobsAndCostCodes;
  if (includeClosedJobs !== undefined) config.params.includeClosedJobs = includeClosedJobs;
  if (includeClosedCostCodes !== undefined) config.params.includeClosedCostCodes = includeClosedCostCodes;
  if (includeDisabled !== undefined) config.params.includeDisabled = includeDisabled;

  return axiosInstance.get(url, config).then((response) => response.data.map((user) => new Employee(user)));
};

export const getUser = (
  userId: string,
  includeCards?: boolean,
  includeDirectReports?: boolean,
  includeFiles?: boolean,
  includeJobsAndCostCodes?: boolean,
  includeClosedCostCodes?: boolean,
  token?: CancelToken
): Promise<Employee> => {
  const url = '/accounts/' + userId;

  var config = {
    headers: {},
    cancelToken: token !== undefined ? token : undefined,
    params: {} as any,
  };

  if (includeCards !== undefined) config.params.includeCards = includeCards;
  if (includeDirectReports !== undefined) config.params.includeDirectReports = includeDirectReports;
  if (includeFiles !== undefined) config.params.includeFiles = includeFiles;
  if (includeJobsAndCostCodes !== undefined) config.params.includeJobsAndCostCodes = includeJobsAndCostCodes;
  if (includeClosedCostCodes !== undefined) config.params.includeClosedCostCodes = includeClosedCostCodes;

  return axiosInstance.get(url, config).then((response) => new Employee(response.data));
};

export const getMe = (token?: CancelToken): Promise<Employee> => {
  const url = '/accounts/me';

  var config = { headers: {}, cancelToken: token !== undefined ? token : undefined };

  return axiosInstance.get(url, config).then((response) => new Employee(response.data));
};

export const postUser = (user: CreateUser): Promise<Employee> => {
  const url = '/accounts';

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  let body =
    'FirstName=' +
    encodeURIComponent(user.firstName) +
    '&LastName=' +
    encodeURIComponent(user.lastName) +
    '&Username=' +
    encodeURIComponent(user.userName) +
    '&EmployeeNumber=' +
    encodeURIComponent(user.employeeNumber) +
    '&Email=' +
    encodeURIComponent(user.email) +
    '&Company=' +
    encodeURIComponent(user.company) +
    '&Password=' +
    encodeURIComponent(user.password ?? '');

  return axiosInstance.post(url, body, configAuth).then((response) => new Employee(response.data));
};

export const putUser = (userId: string, user: EditUser): Promise<Employee> => {
  const url = '/accounts/' + userId;

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  let body =
    'FirstName=' +
    encodeURIComponent(user.firstName) +
    '&LastName=' +
    encodeURIComponent(user.lastName) +
    '&Username=' +
    encodeURIComponent(user.userName) +
    '&EmployeeNumber=' +
    encodeURIComponent(user.employeeNumber) +
    '&Email=' +
    encodeURIComponent(user.email) +
    '&Company=' +
    encodeURIComponent(user.company) +
    '&Disabled=' +
    encodeURIComponent(user.disabled);

  return axiosInstance.put(url, body, configAuth).then((response) => new Employee(response.data));
};

export const deleteUser = (userId: string, token?: CancelToken): Promise<void> => {
  const url = '/accounts/' + userId;

  var config = { headers: {}, cancelToken: token !== undefined ? token : undefined };

  return axiosInstance.delete(url, config).then((response) => response.data);
};

export const putUserDirectReport = (userId: string, directReportId: string): Promise<Employee> => {
  const url = `/accounts/${userId}/directreports/${directReportId}`;
  return axiosInstance.put(url).then((response) => new Employee(response.data));
};

export const deleteUserDirectReport = (userId: string, directReportId: string, token?: CancelToken): Promise<void> => {
  const url = `/accounts/${userId}/directreports/${directReportId}`;
  var config = {
    headers: {},
    cancelToken: token !== undefined ? token : undefined,
  };
  return axiosInstance.delete(url, config).then((response) => response.data);
};

export const getDirectReports = (
  userId: string,
  includeCards?: boolean,
  includeFiles?: boolean,
  includeJobsAndCostCodes?: boolean,
  includeClosedCostCodes?: boolean,
  token?: CancelToken
): Promise<Employee[]> => {
  const url = `/accounts/${userId}/directreports`;
  var config = {
    cancelToken: token !== undefined ? token : undefined,
    params: {} as any,
  };

  if (includeCards !== undefined) config.params.includeCards = includeCards;
  if (includeFiles !== undefined) config.params.includeFiles = includeFiles;
  if (includeJobsAndCostCodes !== undefined) config.params.includeJobsAndCostCodes = includeJobsAndCostCodes;
  if (includeClosedCostCodes !== undefined) config.params.includeClosedCostCodes = includeClosedCostCodes;

  return axiosInstance.get(url, config).then((response) => response.data.map((d) => new Employee(d)));
};

/*------------------------------------Account Maintenance-------------------------------------------*/

export const sendEmailConfirmation = (userId: string): Promise<boolean> => {
  const url = '/accounts/' + userId + '/sendemailconfirmation';

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  return axiosInstance.post(url, configAuth).then((response) => response.data);
};

export const confirmEmail = (userId: string, token: string): Promise<boolean> => {
  const url = '/accounts/' + userId + '/confirmemail';

  var body = 'Token=' + token;

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  return axiosInstance.post(url, body, configAuth).then((response) => response.data);
};

export const sendPasswordReset = (username: string): Promise<boolean> => {
  const url = '/accounts/sendpasswordreset';

  let body = 'Username=' + encodeURIComponent(username);

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  return axiosInstance.post(url, body, configAuth).then((response) => response.data);
};

export const resetPassword = (username: string, token: string, newPassword: string): Promise<boolean> => {
  const url = '/accounts/resetpassword';

  let body = 'Username=' + encodeURIComponent(username) + '&Token=' + token + '&NewPassword=' + encodeURIComponent(newPassword);

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  return axiosInstance.post(url, body, configAuth).then((response) => response.data);
};

export const changePassword = (userId: string, currentPassword: string, newPassword: string): Promise<boolean> => {
  const url = '/accounts/' + userId + '/changepassword';

  let body = 'CurrentPassword=' + encodeURIComponent(currentPassword) + '&NewPassword=' + encodeURIComponent(newPassword);

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  return axiosInstance.post(url, body, configAuth).then((response) => response.data);
};

export const adminChangePassword = (userId: string, newPassword: string): Promise<boolean> => {
  const url = '/accounts/' + userId + '/adminchangepassword';

  let body = 'NewPassword=' + encodeURIComponent(newPassword);

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  return axiosInstance.post(url, body, configAuth).then((response) => response.data);
};

export const putUserRole = (userId: string, roleName: string): Promise<Role> => {
  const url = '/accounts/' + userId + '/roles/' + roleName;

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  return axiosInstance.put(url, configAuth).then((response) => response.data);
};

export const deleteUserRole = (userId: string, roleName: string): Promise<Role> => {
  const url = '/accounts/' + userId + '/roles/' + roleName;

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  return axiosInstance.delete(url, configAuth).then((response) => response.data);
};

/*--------------------------------------Roles---------------------------------------------*/
export const getRoles = (token?: CancelToken): Promise<Role[]> => {
  const url = '/roles';
  var config = { headers: {}, cancelToken: token !== undefined ? token : undefined };
  return axiosInstance.get(url, config).then((response) => response.data);
};

export const getRole = (roleId: string, token?: CancelToken): Promise<Role> => {
  const url = '/roles/' + roleId;
  var config = { headers: {}, cancelToken: token !== undefined ? token : undefined };
  return axiosInstance.get(url, config).then((response) => response.data);
};

export const postRoles = (userId: string): Promise<Role> => {
  const url = '/roles';

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  let body = 'Name=' + encodeURIComponent(userId);

  return axiosInstance.post(url, body, configAuth).then((response) => response.data);
};

export const putRoles = (userId: string, roleId: string): Promise<Role> => {
  const url = '/roles/' + roleId;

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  let body = 'Name=' + encodeURIComponent(userId);

  return axiosInstance.put(url, body, configAuth).then((response) => response.data);
};

export const deleteRole = (roleId: string): Promise<void> => {
  const url = '/roles/' + roleId;
  return axiosInstance.delete(url).then((response) => response.data);
};

/*------------------------------------Claims-------------------------------------------*/

export const getClaimsForUser = (userId: string, token?: CancelToken): Promise<Claim[]> => {
  const url = '/accounts/' + userId + '/claims';

  let configAuth = {
    cancelToken: token !== undefined ? token : undefined,
  };

  return axiosInstance.get(url, configAuth).then((response) => response.data);
};

export const getUsersByClaim = (claimType: string, claimValue?: string, token?: CancelToken): Promise<Employee[]> => {
  const url = `/accounts/claims/${claimType}` + (claimValue ? `/${claimValue}` : '');

  let configAuth = {
    cancelToken: token !== undefined ? token : undefined,
  };

  return axiosInstance.get(url, configAuth).then((response) => response.data);
};

export const putClaim = (userId: string, claim: Claim): Promise<Claim> => {
  const url = '/accounts/' + userId + '/claims';

  let configAuth = {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  };

  let body = 'claimType=' + encodeURIComponent(claim.claimType) + '&claimValue=' + encodeURIComponent(claim.claimValue);

  return axiosInstance.put(url, body, configAuth).then((response) => response.data);
};

export const deleteClaimForUser = (userId: string, ClaimType: string): Promise<void> => {
  const url = '/accounts/' + userId + '/claims?claimType=' + encodeURIComponent(ClaimType);

  //let body = 'claimType=' + encodeURIComponent(ClaimType);

  return axiosInstance.delete(url).then((response) => response.data);
};

/*------------------------------------Default Claims-------------------------------------------*/

export const getDefaultClaims = (claimType?: string, claimValue?: string, token?: CancelToken): Promise<DefaultClaim[]> => {
  const url = '/default/claims';

  let config: AxiosRequestConfig = {
    params: {},
  };

  if (token) config.cancelToken = token;
  if (claimType !== undefined) config.params.claimType = encodeURIComponent(claimType);
  if (claimValue) config.params.tagValue = encodeURIComponent(claimValue);

  return axiosInstance.get(url, config).then((response) => response.data);
};

export const putDefaultClaim = (claim: DefaultClaim): Promise<DefaultClaim> => {
  const url = '/default/claims';
  return axiosInstance.put(url, claim).then((response) => response.data);
};

export const deleteDefaultClaim = (id: number): Promise<void> => {
  const url = `/default/claims/${id}`;
  return axiosInstance.delete(url).then((response) => response.data);
};

export const defaultClaims: DefaultClaim[] = [
  { id: 1, claimType: 'Skill', claimValue: 'Electrician' },
  { id: 2, claimType: 'Skill', claimValue: 'Mechanical' },
  { id: 3, claimType: 'Skill', claimValue: 'Install' },
];

/*------------------------------------System-------------------------------------------*/

export const systemStatus = (): Promise<Date> => {
  const url = '/status';

  return axiosInstance.post(url).then((response) => response.data);
};

/* -------------------- User Management -------------------- */
export interface BaseUser {
  firstName: string;
  lastName: string;
  userName: string;
  employeeNumber: string;
  email: string;
  company: string;
}

export interface CreateUser extends BaseUser {
  password?: string;
}

export interface User extends BaseUser {
  id: string;
  joinDate: Date;
  lastLogin: Date;
  roles: Role[];
  claims: Claim[];
  emailConfirmed: boolean;
  disabled: boolean;
  directReports: User[];
  managers: User[];
  files: FileUploads[];
  jobs: CYMAJob[];
  earningsTypes: EarningsType[];
  skills: Skill[];
  costCodes: CostCode[];
  creditCards: CreditCardAssignment[];
  creditCardTransactions: CreditCardTransaction[];
  expenseReports: ExpenseReportHeader[];
  timesheets: TimeRecordHeader[];
}

export class Employee implements User {
  id: string;
  firstName: string;
  lastName: string;
  userName: string;
  employeeNumber: string;
  email: string;
  company: string;
  joinDate: Date;
  lastLogin: Date;
  _roles: Role[];
  claims: Claim[];
  emailConfirmed: boolean;
  disabled: boolean;
  directReports: Employee[];
  managers: Employee[];
  files: FileUploads[];
  jobs: CYMAJob[];
  earningsTypes: EarningsType[];
  skills: Skill[];
  costCodes: CostCode[];
  creditCards: CreditCardAssignment[];
  creditCardTransactions: CreditCardTransaction[];

  isFake: boolean = false;
  isImpersonating: boolean = false;

  expenseReportsObj: ExpenseReport[];
  timesheetsObj: Timesheet[];

  originalRoles: Role[];
  strRoles: string[];
  hideAdmin: boolean;

  get expenseReports() {
    return this.expenseReportsObj.map((e) => e._header);
  }

  get timesheets() {
    return this.timesheetsObj.map((e) => e._header);
  }

  set roles(roles: Role[]) {
    this._roles = roles;
    this.strRoles = roles?.map((r) => r.name?.toLowerCase()) ?? [];
  }

  get roles() {
    return this._roles;
  }

  get fullName() {
    return this.lastName && this.firstName ? `${this.firstName} ${this.lastName}` : this.userName;
  }

  constructor(user: User | Employee) {
    this.id = user.id;
    this.firstName = user.firstName;
    this.lastName = user.lastName;
    this.userName = user.userName;
    this.employeeNumber = user.employeeNumber;
    this.email = user.email;
    this.company = user.company;
    this.joinDate = user.joinDate;
    this.lastLogin = user.lastLogin;
    this._roles = user.roles;
    this.claims = user.claims;
    this.emailConfirmed = user.emailConfirmed;
    this.disabled = user.disabled;

    this.isFake = user?.['isFake'] ?? false;
    this.isImpersonating = user?.['isImpersonating'] ?? false;

    this.directReports = (user.directReports as User[])?.map((dr) => new Employee(dr)) ?? [];
    this.managers = (user.managers as User[])?.map((mn) => new Employee(mn)) ?? [];
    this.files = user.files;
    this.jobs = user.jobs;
    this.skills = user.skills?.sort((a, b) => (a.name < b.name ? -1 : 1));
    this.earningsTypes = user.earningsTypes?.sort((a, b) => (a.longName === 'Hourly' ? -1 : b.longName === 'Hourly' ? 1 : a.longName < b.longName ? -1 : 1)) ?? [];
    this.costCodes = user.costCodes;
    this.creditCards = user.creditCards;
    this.creditCardTransactions = user.creditCardTransactions;

    this.expenseReportsObj =
      user.expenseReports?.map((e) => {
        const er = new ExpenseReport(this, new Date(Date.parse(e.periodEndingDate?.toString() ?? '')), user.jobs);
        er.data = e;
        return er;
      }) ?? [];
    this.timesheetsObj =
      user.timesheets?.map((t) => {
        const ts = new Timesheet(this, new Date(Date.parse(t.periodEndingDate?.toString() ?? '')), user.jobs, user.earningsTypes);
        ts.data = t;
        return ts;
      }) ?? [];

    this.strRoles = user.roles?.map((r) => r.name?.toLowerCase()) ?? [];
    this.roles = user.roles;
    this.originalRoles = [...(this._roles ?? [])];
    this.hideAdmin = false;
  }

  static empty(val: any) {
    return new Employee({
      isFake: true,
      ...val,
    });
  }

  wasInRole(role: string): boolean {
    return (this?.originalRoles?.findIndex((r) => r.name.toLowerCase() === role.toLowerCase()) ?? -1) > -1;
  }

  isInRole(role: string): boolean {
    return this.strRoles.filter((str) => !(this.hideAdmin && str === 'admin')).indexOf(role?.toLowerCase()) > -1;
  }

  isInRoles(roles: string[]): boolean {
    return this.strRoles.filter((str) => !(this.hideAdmin && str === 'admin')).findIndex((strRoles) => roles?.findIndex((r) => r.toLowerCase() === strRoles) > -1) > -1;
  }

  addRole(role: string) {
    this.strRoles.push(role.toLowerCase());
  }

  setRoles(roles: string[]) {
    this.strRoles = roles.map((r) => r.toLowerCase());
  }

  removeRole(role: string) {
    const index = this.strRoles.findIndex((strRole) => strRole === role.toLowerCase());
    if (index > -1) this.strRoles.splice(index, 1);
  }

  resetRoles() {
    this.roles = this.originalRoles;
    this.strRoles = this.originalRoles.map((r) => r.name?.toLowerCase());
  }

  getClaim(type: string, caseSensitive: boolean = false): Claim | undefined {
    return this.claims?.find((c) => (caseSensitive && c.claimType === type) || c.claimType.toLowerCase() === type.toLowerCase());
  }

  getClaims(type: string, caseSensitive: boolean = false): Claim[] | undefined {
    return this.claims?.filter((c) => (caseSensitive && c.claimType === type) || c.claimType.toLowerCase() === type.toLowerCase());
  }

  getClaimValue(type: string): string | undefined {
    return this.claims?.find((c) => c.claimType === type)?.claimValue;
  }

  hasClaim(type: string, value: string, caseSensitive: boolean = false): boolean {
    let claim = this.getClaim(type, caseSensitive);
    return claim !== undefined && ((caseSensitive && claim.claimValue === value) || claim.claimValue.toLowerCase() === value.toLowerCase());
  }

  hasSkillClaim() {
    return this.getClaim('skill', false) ? true : false;
  }

  addClaim(claim: Claim) {
    if (this.claims) this.claims.push(claim);
    else this.claims = [claim];
  }

  removeClaim(id: string): Claim | undefined {
    let index = this.claims.findIndex((claim) => claim.id === id);
    if (index === -1) return;
    return this.claims.splice(index, 1)[0];
  }
}

export interface EditUser extends BaseUser {
  id: string;
  emailConfirmed: boolean;
  disabled: boolean;
}

export interface Claim {
  id: string;
  userId: string;
  claimType: string;
  claimValue: string;
}

export interface DefaultClaim extends CreatedUpdatedTable {
  id: number;
  claimType: string;
  claimValue: string;
}

export interface Role {
  roleId: string;
  name: string;
}
